import {Formik} from 'formik'
import {useState} from 'react'
import {Form} from 'react-bootstrap'
import * as Yup from 'yup'
import {ConnectedFocusError} from 'focus-formik-error'

import {InlineTable} from 'src/feature/bite/component'
import {useUpdateStoreContact} from 'src/feature/store/context/useMutation'

const LocationContact = ({loading, intl, detail}: {loading: boolean; intl: any; detail: any}) => {
  const [editable, setEditable] = useState(false)
  const {mutateAsync, isLoading} = useUpdateStoreContact({storeId: detail?.id || ''})

  const column = [
    [
      {
        key: 'firstname',
        dataIndex: 'firstname',
        required: true,
        tooltip: 'Enter the first name of the contact person for this location.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.NAME'}),
        edit: 'input',
        maxLength: 51,
      },
      {
        key: 'phone',
        dataIndex: 'phone',
        formName: 'phone',
        tooltip: 'Provide a phone number for inquiries and business communication.',
        required: true,
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.PHONE'}),
        edit: 'phone',
      },
      {
        key: 'title',
        dataIndex: 'title',
        tooltip: 'Specify the title or role of the contact person.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.TITLE'}),
        edit: 'input',
        maxLength: 51,
      },
    ],
    [
      {
        key: 'lastname',
        dataIndex: 'lastname',
        required: true,
        tooltip: 'Enter the last name of the contact person.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.SURNAME'}),
        edit: 'input',
        maxLength: 51,
      },
      {
        key: 'email',
        dataIndex: 'email',
        required: true,
        tooltip: 'Enter the official email address for this location.',
        title: intl.formatMessage({id: 'STORE.DETAIL.CONTACT.EMAIL'}),
        edit: 'input',
        maxLength: 101,
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    firstname: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    lastname: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    phone: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    email: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    title: Yup.string(),
  })

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        userID: detail?.userID || '',
        firstname: detail?.Contact?.firstname || '',
        lastname: detail?.Contact?.lastname || '',
        phone: detail?.Contact?.phone || '',
        countryPhoneCode: detail?.countryPhoneCode || '+971',
        culture: detail?.culture || 'AE',
        email: detail?.Contact?.email || '',
        title: detail?.Contact?.titleName || '',
      }}
      onSubmit={async (values) => {
        values.countryPhoneCode = '+971'
        values.culture = 'AE'
        const updateResponse = await mutateAsync({input: values})
        if (updateResponse) {
          setEditable(false)
        }
      }}
      validateOnMount
    >
      {({errors, resetForm, handleSubmit, values}) => {
        return (
          <Form>
            <ConnectedFocusError />
            <div className='card mb-5 mb-xl-10'>
              <div className='card-header'>
                <div className='card-title m-0'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>
                      {intl.formatMessage({id: 'STORE.DETAIL.CONTACT'})}
                    </h3>
                    <p className='text-muted fw-normal fs-6'>
                      {intl.formatMessage({id: 'STORE.DETAIL.CONTACT_TOOLTIP'})}
                    </p>
                  </div>
                </div>
              </div>

              <div className='card-body p-9'>
                <InlineTable
                  loading={loading}
                  dataSource={values}
                  columns={column}
                  editable={editable}
                  values={values}
                />
              </div>
              <div className='card-footer d-flex w-100 justify-content-end'>
                {editable ? (
                  <div>
                    <button
                      type='button'
                      className='btn btn-primary'
                      style={{marginRight: 10}}
                      onClick={(e) => {
                        e.preventDefault()
                        setEditable(false)
                        resetForm()
                      }}
                    >
                      <i className='bi bi-x me-2'></i>
                      {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary'
                      disabled={loading || isLoading}
                      onClick={async (e) => {
                        await handleSubmit()
                      }}
                    >
                      <i className='bi bi-save2 me-2'></i>
                      {loading
                        ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                        : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                    </button>
                  </div>
                ) : (
                  <button className='btn btn-primary' onClick={() => setEditable(true)}>
                    <i className='bi bi-pencil me-2'></i>
                    {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                  </button>
                )}
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default LocationContact
